import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { MessageSquare, Bell } from "lucide-react";
import { api } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { formatDateTime, STATUS_LABEL } from "@/lib/format";
import TicketThread from "@/components/TicketThread";

export default function AdminTickets() {
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/tickets");
      setItems(data);
    } catch (err) {
      console.error("failed to load tickets", err);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const unreadTotal = useMemo(
    () => items.reduce((sum, t) => sum + (t.unread_by_support || 0), 0),
    [items]
  );

  const tone = (s) => {
    if (s === "open") return "bg-amber-100 text-amber-800 border-amber-200";
    if (s === "analyzing") return "bg-blue-100 text-blue-800 border-blue-200";
    if (s === "answered") return "bg-emerald-100 text-emerald-800 border-emerald-200";
    if (s === "resolved") return "bg-emerald-100 text-emerald-800 border-emerald-200";
    if (s === "closed") return "bg-stone-200 text-stone-700";
    return "bg-stone-100 text-stone-700";
  };

  return (
    <Layout
      title="Suporte"
      subtitle="Tickets enviados por empresas"
      actions={
        unreadTotal > 0 && (
          <Badge className="bg-rose-600 text-white border-none" data-testid="admin-tickets-unread">
            <Bell className="h-3.5 w-3.5 mr-1" /> {unreadTotal} nova(s)
          </Badge>
        )
      }
    >
      <Card data-testid={TID.adminTickets} className="rounded-xl border-stone-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="font-body">Assunto</TableHead>
              <TableHead className="font-body">Prioridade</TableHead>
              <TableHead className="font-body">Status</TableHead>
              <TableHead className="font-body">Última atividade</TableHead>
              <TableHead className="font-body text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-stone-500 py-10">
                  <MessageSquare className="h-6 w-6 mx-auto mb-2 text-stone-400" />
                  Nenhum ticket
                </TableCell>
              </TableRow>
            )}
            {items.map((t) => (
              <TableRow
                key={t.id}
                data-testid={`${TID.adminTicketRow}-${t.id}`}
                className={t.unread_by_support > 0 ? "bg-amber-50/40" : ""}
              >
                <TableCell>
                  <div className="font-display font-semibold flex items-center gap-2">
                    {t.subject}
                    {t.unread_by_support > 0 && (
                      <Badge className="bg-rose-600 text-white text-[10px] border-none">
                        {t.unread_by_support} nova(s)
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-stone-500 font-body line-clamp-1">{t.message}</div>
                </TableCell>
                <TableCell className="font-body text-sm capitalize">{t.priority}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={tone(t.status)}>
                    {STATUS_LABEL[t.status] || t.status}
                  </Badge>
                </TableCell>
                <TableCell className="font-body text-sm">
                  {formatDateTime(t.last_activity_at || t.updated_at || t.created_at)}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedId(t.id)}
                    data-testid={`ticket-open-${t.id}`}
                  >
                    Abrir conversa
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <TicketThread
        ticketId={selectedId}
        open={!!selectedId}
        onOpenChange={(o) => !o && setSelectedId(null)}
        onUpdated={load}
        showAdminControls
        viewerRole="support"
      />
    </Layout>
  );
}
